import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { ProductCard } from "../components/ProductCard";
import { ArrowLeft, Gavel, ShoppingBag, Clock, CheckCircle } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";

// Section configuration
const SECTIONS = {
  auctions: {
    title: "Subastas Activas",
    subtitle: "Oferta y gana",
    icon: Gavel,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    fetchUrl: "/products?sale_type=auction",
    emptyMessage: "No hay subastas activas en este momento"
  },
  direct: {
    title: "Venta Directa",
    subtitle: "Reserva al instante",
    icon: ShoppingBag,
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    fetchUrl: "/products?sale_type=direct",
    emptyMessage: "No hay productos en venta directa"
  },
  scheduled: {
    title: "Próximamente",
    subtitle: "Activa recordatorios",
    icon: Clock,
    color: "text-violet-600",
    bgColor: "bg-violet-100",
    fetchUrl: "/products/scheduled",
    emptyMessage: "No hay productos programados"
  },
  sold: {
    title: "Finalizados",
    subtitle: "Historial de ventas",
    icon: CheckCircle,
    color: "text-gray-500",
    bgColor: "bg-gray-200",
    fetchUrl: "/products/sold",
    emptyMessage: "No hay productos finalizados"
  }
};

const SectionPage = () => {
  const { sectionType } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auctionStatus, setAuctionStatus] = useState([]);

  const section = SECTIONS[sectionType];

  useEffect(() => {
    if (!section) {
      navigate("/");
      return;
    }
    fetchProducts();
  }, [sectionType]);

  useEffect(() => {
    if (user && token && sectionType === "auctions") {
      fetchAuctionStatus();
    }
  }, [user, token, sectionType]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}${section.fetchUrl}`;
      // Add timestamp to prevent caching
      url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
      
      const response = await axios.get(url);
      let data = response.data || [];
      
      // Filter based on section type
      if (sectionType === "auctions") {
        data = data.filter(p => p.sale_type === "auction" && !p.is_sold);
      } else if (sectionType === "direct") {
        data = data.filter(p => p.sale_type === "direct" && !p.is_sold);
      }
      
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuctionStatus = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/my/auction-status`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAuctionStatus(response.data);
    } catch (error) {
      // Silently fail
    }
  };

  if (!section) {
    return null;
  }

  const Icon = section.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      <main className="pb-20">
        {/* Section Header */}
        <div className="bg-white border-b border-[#E2E8F0] sticky top-[104px] z-40">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 hover:bg-[#F1F5F9] rounded-full transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft size={20} className="text-[#64748B]" />
              </button>
              <div className={`w-10 h-10 ${section.bgColor} rounded-xl flex items-center justify-center`}>
                <Icon size={20} className={section.color} />
              </div>
              <div>
                <h1 className="font-bold text-lg text-[#0F172A]">{section.title}</h1>
                <p className="text-xs text-[#64748B]">{section.subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid - 2 columns Instagram style */}
        <div className="max-w-4xl mx-auto px-4 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <div className={`w-16 h-16 ${section.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Icon size={32} className={section.color} />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">Sin productos</h3>
              <p className="text-sm text-[#64748B] mt-2">{section.emptyMessage}</p>
              <Link 
                to="/"
                className="inline-block mt-4 px-6 py-2 bg-[#475569] text-white rounded-full text-sm font-medium hover:bg-[#334155] transition-colors"
              >
                Volver al inicio
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.product_id}
                  product={product}
                  compact={true}
                  auctionStatus={auctionStatus}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default SectionPage;
